import { Component, DestroyRef, ElementRef, OnDestroy, OnInit, ViewChild, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { EMPTY, Subscription, switchMap, timer } from 'rxjs';
import { AuthService } from '../../../core/services/auth.service';
import { ChatApiService, VISITOR_CHAT_STORAGE } from '../../../core/services/chat-api.service';
import { ChatRealtimeService } from '../../../core/services/chat-realtime.service';
import { LanguageService } from '../../../core/i18n/language.service';
import { ChatMessage } from '../../../core/models/chat.model';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';

const GREETING_ID = 'local-greeting';
const AUTO_ENGAGE_DELAY_MS = 10_000;
/** Temporary REST fallback only while SignalR is fully closed. */
const FALLBACK_POLL_MS = 8_000;

@Component({
  selector: 'app-visitor-chat-widget',
  standalone: true,
  imports: [FormsModule, TranslatePipe],
  templateUrl: './visitor-chat-widget.component.html',
  styleUrl: './visitor-chat-widget.component.scss'
})
export class VisitorChatWidgetComponent implements OnInit, OnDestroy {
  @ViewChild('thread') private threadRef?: ElementRef<HTMLElement>;

  private readonly api = inject(ChatApiService);
  private readonly realtime = inject(ChatRealtimeService);
  private readonly i18n = inject(LanguageService);
  private readonly auth = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  readonly visible = signal(false);
  readonly open = signal(false);
  readonly loading = signal(false);
  readonly sending = signal(false);
  readonly messages = signal<ChatMessage[]>([]);
  readonly draft = signal('');
  readonly error = signal<string | null>(null);

  private visitorKey = '';
  private conversationId: string | null = null;
  private autoEngageTimer: ReturnType<typeof setTimeout> | null = null;
  private messageSub: Subscription | null = null;
  private engageInFlight = false;
  private visitorHasSentMessage = false;
  /** Reused across retries of the same logical send; cleared on success or draft change. */
  private pendingClientMessageId: string | null = null;
  private pendingClientMessageBody: string | null = null;

  constructor() {
    effect(() => {
      const _lang = this.i18n.lang();
      this.messages.update((list) => {
        if (!list.some((m) => m.id === GREETING_ID)) {
          return list;
        }
        return list.map((m) =>
          m.id === GREETING_ID ? { ...m, body: this.i18n.t('chat.visitor.greeting') } : m
        );
      });
      void _lang;
    });

    effect(() => {
      const count = this.messages().length;
      const isOpen = this.open();
      if (count > 0 && isOpen) {
        queueMicrotask(() => this.scrollToBottom());
      }
    });
  }

  ngOnInit(): void {
    if (this.auth.isLoggedIn()) {
      return;
    }

    this.visitorKey = this.ensureVisitorKey();
    this.conversationId = localStorage.getItem(VISITOR_CHAT_STORAGE.conversationId);

    this.messageSub = this.realtime.messageReceived$.subscribe((msg) => {
      if (!this.conversationId || msg.conversationId !== this.conversationId) {
        return;
      }
      if (msg.senderType !== 'Admin') {
        return;
      }
      this.appendMessage(msg);
      this.visible.set(true);
      if (!this.open()) {
        this.open.set(true);
      }
    });

    // After SignalR reconnect: one REST sync, then realtime only.
    this.messageSub.add(
      this.realtime.visitorSyncNeeded$.subscribe(() => {
        this.syncMessagesOnce();
      })
    );

    // Fallback polling only when hub is closed (not during automatic reconnect).
    this.messageSub.add(
      this.realtime.visitorFallback$
        .pipe(
          switchMap((active) =>
            active ? timer(FALLBACK_POLL_MS, FALLBACK_POLL_MS) : EMPTY
          )
        )
        .subscribe(() => {
          if (!this.open() || !this.conversationId) {
            return;
          }
          this.syncMessagesOnce();
        })
    );

    this.visible.set(true);
    this.scheduleAutoEngage(AUTO_ENGAGE_DELAY_MS);
  }

  ngOnDestroy(): void {
    this.clearEngageTimers();
    this.messageSub?.unsubscribe();
    void this.realtime.stopVisitor();
    if (this.auth.isLoggedIn() && this.visitorKey) {
      this.api
        .visitorAbandon({
          visitorKey: this.visitorKey,
          conversationId: this.conversationId
        })
        .subscribe({ error: () => undefined });
    }
  }

  toggle(): void {
    const next = !this.open();
    this.open.set(next);
    this.visible.set(true);
    if (next) {
      void this.bootstrapThread(false);
    }
  }

  async send(): Promise<void> {
    const body = this.draft().trim();
    if (!body || this.sending()) {
      return;
    }

    if (this.pendingClientMessageBody !== body) {
      this.pendingClientMessageId = crypto.randomUUID();
      this.pendingClientMessageBody = body;
    }
    const clientMessageId = this.pendingClientMessageId!;

    this.sending.set(true);
    this.error.set(null);
    this.api
      .visitorSend({
        visitorKey: this.visitorKey,
        body,
        conversationId: this.conversationId,
        clientMessageId
      })
      .subscribe({
        next: async (result) => {
          this.pendingClientMessageId = null;
          this.pendingClientMessageBody = null;
          this.conversationId = result.conversation.id;
          localStorage.setItem(VISITOR_CHAT_STORAGE.conversationId, result.conversation.id);
          this.appendMessage(result.message);
          this.draft.set('');
          this.visitorHasSentMessage = true;
          this.cancelAutoEngageTimer();
          this.sending.set(false);
          await this.realtime.startVisitor(result.conversation.id, this.visitorKey);
        },
        error: (err) => {
          const message = err?.error?.error ?? 'Unable to send message.';
          this.error.set(message);
          this.sending.set(false);
          if (typeof message === 'string' && /closed/i.test(message)) {
            void this.clearStaleConversation();
          }
        }
      });
  }

  private scheduleAutoEngage(delayMs: number): void {
    if (this.auth.isLoggedIn() || this.visitorHasSentMessage) {
      return;
    }
    this.cancelAutoEngageTimer();
    this.autoEngageTimer = setTimeout(() => this.autoEngageIfIdle(), delayMs);
  }

  private autoEngageIfIdle(): void {
    if (this.auth.isLoggedIn() || this.visitorHasSentMessage) {
      return;
    }
    this.engage();
  }

  private cancelAutoEngageTimer(): void {
    if (this.autoEngageTimer) {
      clearTimeout(this.autoEngageTimer);
      this.autoEngageTimer = null;
    }
  }

  private clearEngageTimers(): void {
    this.cancelAutoEngageTimer();
  }

  private engage(): void {
    if (this.auth.isLoggedIn()) {
      return;
    }
    this.visible.set(true);
    this.open.set(true);
    void this.bootstrapThread(true);
  }

  private async bootstrapThread(fromEngage: boolean): Promise<void> {
    if (this.conversationId) {
      this.loading.set(true);
      this.api.visitorMessages(this.conversationId, this.visitorKey, { silent: true }).subscribe({
        next: async (messages) => {
          this.messages.set(messages.length > 0 ? messages : [this.greetingMessage()]);
          this.loading.set(false);
          await this.realtime.startVisitor(this.conversationId!, this.visitorKey);
        },
        error: () => {
          void this.clearStaleConversation();
          this.loading.set(false);
          if (fromEngage) {
            void this.runEngage();
          } else {
            this.messages.set([this.greetingMessage()]);
          }
        }
      });
      return;
    }

    if (fromEngage) {
      await this.runEngage();
      return;
    }

    this.messages.set([this.greetingMessage()]);
  }

  private async runEngage(): Promise<void> {
    if (this.engageInFlight || this.auth.isLoggedIn()) {
      return;
    }
    this.engageInFlight = true;
    this.loading.set(true);
    this.error.set(null);

    this.api
      .visitorEngage({
        visitorKey: this.visitorKey,
        greetingBody: this.i18n.t('chat.visitor.greeting')
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: async (result) => {
          const id = result.conversation?.id;
          if (
            this.auth.isLoggedIn() ||
            !id ||
            result.conversation.status === 'Closed'
          ) {
            this.loading.set(false);
            this.engageInFlight = false;
            return;
          }
          this.conversationId = result.conversation.id;
          localStorage.setItem(VISITOR_CHAT_STORAGE.conversationId, result.conversation.id);
          this.messages.set(
            result.messages.length > 0 ? result.messages : [this.greetingMessage()]
          );
          this.loading.set(false);
          this.engageInFlight = false;
          await this.realtime.startVisitor(result.conversation.id, this.visitorKey);
        },
        error: (err) => {
          this.messages.set([this.greetingMessage()]);
          this.error.set(err?.error?.error ?? null);
          this.loading.set(false);
          this.engageInFlight = false;
        }
      });
  }

  private greetingMessage(): ChatMessage {
    return {
      id: GREETING_ID,
      conversationId: this.conversationId ?? '',
      body: this.i18n.t('chat.visitor.greeting'),
      senderType: 'Admin',
      senderUserId: null,
      createdAt: new Date().toISOString(),
      isReadByAdmin: true
    };
  }

  private lastBackendMessageId(): string | null {
    for (let i = this.messages().length - 1; i >= 0; i--) {
      const id = this.messages()[i].id;
      if (id && id !== GREETING_ID) {
        return id;
      }
    }
    return null;
  }

  private syncMessagesOnce(): void {
    if (!this.conversationId) {
      return;
    }
    const afterMessageId = this.lastBackendMessageId();
    this.api
      .visitorMessages(this.conversationId, this.visitorKey, {
        silent: true,
        afterMessageId
      })
      .subscribe({
        next: (messages) => {
          if (messages.length === 0) {
            return;
          }
          this.messages.update((current) => {
            const greeting = current.find((m) => m.id === GREETING_ID);
            const byId = new Map(current.filter((m) => m.id !== GREETING_ID).map((m) => [m.id, m]));
            for (const m of messages) {
              byId.set(m.id, m);
            }
            const merged = [...byId.values()].sort(
              (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
            );
            return greeting && merged.length === 0 ? [greeting] : merged;
          });
        },
        error: (err) => {
          const message = err?.error?.error ?? '';
          if (typeof message === 'string' && /closed|not found/i.test(message)) {
            void this.clearStaleConversation();
          }
        }
      });
  }

  private async clearStaleConversation(): Promise<void> {
    localStorage.removeItem(VISITOR_CHAT_STORAGE.conversationId);
    this.conversationId = null;
    await this.realtime.stopVisitor();
    this.messages.set([this.greetingMessage()]);
  }

  private appendMessage(message: ChatMessage): void {
    this.messages.update((list) => {
      if (list.some((m) => m.id === message.id)) {
        return list;
      }
      return [...list, message];
    });
  }

  private ensureVisitorKey(): string {
    const existing = localStorage.getItem(VISITOR_CHAT_STORAGE.visitorKey);
    if (existing && /^[0-9a-f-]{36}$/i.test(existing)) {
      return existing.toLowerCase();
    }
    const key = crypto.randomUUID();
    localStorage.setItem(VISITOR_CHAT_STORAGE.visitorKey, key);
    return key;
  }

  private scrollToBottom(): void {
    const el = this.threadRef?.nativeElement;
    if (!el) {
      return;
    }
    el.scrollTop = el.scrollHeight;
  }
}
